import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Breadcrumbs,
    Link,
    Typography,
    CircularProgress,
    Alert,
    Checkbox,
    Box,
    TextField,
    InputAdornment,
    Divider
} from '@mui/material';
import {
    Folder as FolderIcon,
    InsertDriveFile as FileIcon,
    Home as HomeIcon,
    Search as SearchIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { FddApi } from '../../../ui/services/server-fdd';

/**
 * NextcloudFilePicker Component
 * Browse and select PDF files from Nextcloud
 * 
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close handler
 * @param {function} onSelect - Select handler (receives selected file object)
 * @param {boolean} onlyPDFs - Show only PDF files (default: true)
 * @param {boolean} multiSelect - Allow multiple file selection (default: false)
 */
export const NextcloudFilePicker = ({ 
    open, 
    onClose, 
    onSelect, 
    onlyPDFs = true,
    multiSelect = false 
}) => {
    const [currentPath, setCurrentPath] = useState('/');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Load files when path changes
    useEffect(() => {
        if (open) {
            loadFiles(currentPath);
        }
    }, [currentPath, open]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setCurrentPath('/');
            setSelectedFiles([]);
            setSearchTerm('');
            setError(null);
        }
    }, [open]);

    /**
     * Load files from Nextcloud
     */
    const loadFiles = async (path) => {
        try {
            setLoading(true);
            setError(null);

            const result = await FddApi.browseNextcloudFiles(path, onlyPDFs);

            if (result.hasError) {
                setError(result.message);
                return;
            }

            // Sort: directories first, then files
            const sorted = result.items.sort((a, b) => {
                if (a.type === 'directory' && b.type === 'file') return -1;
                if (a.type === 'file' && b.type === 'directory') return 1;
                return a.basename.localeCompare(b.basename);
            });

            setItems(sorted);
        } catch (error) {
            console.error('[FilePicker] Error loading files:', error);
            setError('Error al cargar archivos de Nextcloud');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Navigate to a directory
     */
    const navigateToFolder = (folderPath) => {
        setCurrentPath(folderPath);
        setSelectedFiles([]);
    };

    /**
     * Handle file/folder click
     */
    const handleItemClick = (item) => {
        if (item.type === 'directory') {
            navigateToFolder(item.filename);
        } else if (item.type === 'file') {
            handleFileSelection(item);
        }
    };

    /**
     * Handle file selection
     */
    const handleFileSelection = (file) => {
        if (multiSelect) {
            const isSelected = selectedFiles.some(f => f.filename === file.filename);
            if (isSelected) {
                setSelectedFiles(selectedFiles.filter(f => f.filename !== file.filename));
            } else {
                setSelectedFiles([...selectedFiles, file]);
            }
        } else {
            setSelectedFiles([file]);
        }
    };

    /**
     * Handle confirm selection
     */
    const handleConfirm = () => {
        if (selectedFiles.length > 0) {
            if (multiSelect) {
                onSelect(selectedFiles);
            } else {
                onSelect(selectedFiles[0]);
            }
            onClose();
        }
    };

    /**
     * Get breadcrumb parts from current path
     */
    const getBreadcrumbs = () => {
        if (currentPath === '/') return [];
        
        const parts = currentPath.split('/').filter(p => p);
        const breadcrumbs = [];
        
        for (let i = 0; i < parts.length; i++) {
            const path = '/' + parts.slice(0, i + 1).join('/');
            breadcrumbs.push({
                label: parts[i],
                path: path
            });
        }
        
        return breadcrumbs;
    };

    /**
     * Filter items by search term
     */
    const getFilteredItems = () => {
        if (!searchTerm) return items;
        
        return items.filter(item => 
            item.basename.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    /**
     * Format file size
     */
    const formatSize = (bytes) => {
        if (!bytes) return '';
        const kb = bytes / 1024;
        if (kb < 1024) return `${kb.toFixed(1)} KB`;
        const mb = kb / 1024;
        return `${mb.toFixed(1)} MB`;
    };

    const filteredItems = getFilteredItems();

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '80vh',
                    maxHeight: '600px'
                }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        Seleccionar {onlyPDFs ? 'PDF' : 'archivo'} de Nextcloud
                    </Typography>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ padding: 0 }}>
                {/* Breadcrumbs Navigation */}
                <Box sx={{ padding: 2, paddingBottom: 1 }}>
                    <Breadcrumbs 
                        separator={<ChevronRightIcon fontSize="small" />}
                        aria-label="breadcrumb"
                    >
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigateToFolder('/')}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none',
                                color: currentPath === '/' ? 'primary.main' : 'text.primary',
                                '&:hover': { textDecoration: 'underline' }
                            }}
                        >
                            <HomeIcon sx={{ mr: 0.5, fontSize: '1.2rem' }} />
                            Inicio
                        </Link>
                        
                        {getBreadcrumbs().map((crumb, index) => (
                            <Link
                                key={crumb.path}
                                component="button"
                                variant="body2"
                                onClick={() => navigateToFolder(crumb.path)}
                                sx={{
                                    textDecoration: 'none',
                                    color: index === getBreadcrumbs().length - 1 ? 'primary.main' : 'text.primary',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                {crumb.label}
                            </Link>
                        ))}
                    </Breadcrumbs>

                    {/* Search Box */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Buscar archivos..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ marginTop: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Divider />

                {/* File List */}
                <Box sx={{ 
                    height: 'calc(80vh - 280px)', 
                    maxHeight: '320px',
                    overflowY: 'auto',
                    padding: 1
                }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Alert severity="error" sx={{ margin: 2 }}>
                            {error}
                        </Alert>
                    ) : filteredItems.length === 0 ? (
                        <Box sx={{ padding: 4, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                {searchTerm ? 'No se encontraron archivos' : 'Esta carpeta está vacía'}
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {filteredItems.map((item) => {
                                const isSelected = selectedFiles.some(f => f.filename === item.filename);
                                const isFile = item.type === 'file';

                                return (
                                    <ListItem
                                        key={item.filename}
                                        button
                                        onClick={() => handleItemClick(item)}
                                        selected={isSelected}
                                        sx={{
                                            borderRadius: 1,
                                            marginBottom: 0.5,
                                            '&:hover': {
                                                backgroundColor: 'action.hover'
                                            }
                                        }}
                                    >
                                        {multiSelect && isFile && (
                                            <ListItemIcon>
                                                <Checkbox
                                                    edge="start"
                                                    checked={isSelected}
                                                    tabIndex={-1}
                                                    disableRipple
                                                />
                                            </ListItemIcon>
                                        )}
                                        
                                        <ListItemIcon>
                                            {item.type === 'directory' ? (
                                                <FolderIcon color="primary" />
                                            ) : (
                                                <FileIcon color="action" />
                                            )}
                                        </ListItemIcon>

                                        <ListItemText
                                            primary={item.basename}
                                            secondary={
                                                isFile ? formatSize(item.size) : 'Carpeta'
                                            }
                                            primaryTypographyProps={{
                                                noWrap: true,
                                                fontWeight: isSelected ? 600 : 400
                                            }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ padding: 2 }}>
                <Typography variant="caption" sx={{ flexGrow: 1, marginLeft: 1 }}>
                    {selectedFiles.length > 0 && (
                        `${selectedFiles.length} archivo${selectedFiles.length > 1 ? 's' : ''} seleccionado${selectedFiles.length > 1 ? 's' : ''}`
                    )}
                </Typography>
                
                <Button onClick={onClose} color="inherit">
                    Cancelar
                </Button>
                
                <Button 
                    onClick={handleConfirm} 
                    variant="contained"
                    disabled={selectedFiles.length === 0}
                >
                    Seleccionar
                </Button>
            </DialogActions>
        </Dialog>
    );
};
